if (!customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    constructor() {
      super();
      this.endDate = this.dataset.endEpoch
        ? Number(this.dataset.endEpoch) * 1000
        : new Date(this.dataset.endDate).getTime();
      if (!Number(this.endDate)) return;

      this.reloadOnEnd = this.hasAttribute('data-reload-on-end');
      this.renderedEndDate = this.endDate;

      // a page we reloaded in the last minute was rendered fresh, so its render time corrects a device clock that runs fast or slow
      const reloadRequestedAt = Number(new URL(window.location.href).searchParams.get(CountdownTimer.reloadParam));
      if (this.reloadOnEnd && this.dataset.renderedAt && reloadRequestedAt && Date.now() - reloadRequestedAt < 60000) {
        this.endDate -= Number(this.dataset.renderedAt) * 1000 - Date.now();
      }

      // reload timers must run wherever they sit on the page, so they skip lazy loading
      if (this.reloadOnEnd) {
        this.init();
      } else if (!this.closest('.marquee-item')) {
        // scrolling banner coordinates loading
        window.initLazyScript(this, this.init.bind(this));
      }
    }

    init() {
      this.compactEl = this.querySelector('.js-compact');
      if (this.compactEl) {
        this.initCompact();
        return;
      }

      this.daysEl = this.querySelector('.js-days .countdown__number-part');
      if (!this.daysEl) {
        this.timer();
        return;
      }
      this.hoursPart1El = this.querySelector('.js-hours .countdown__number-part-1');
      this.hoursPart2El = this.querySelector('.js-hours .countdown__number-part-2');
      this.minsPart1El = this.querySelector('.js-mins .countdown__number-part-1');
      this.minsPart2El = this.querySelector('.js-mins .countdown__number-part-2');
      this.secsPart1El = this.querySelector('.js-secs .countdown__number-part-1');
      this.secsPart2El = this.querySelector('.js-secs .countdown__number-part-2');

      this.second = 1000;
      this.minute = 60 * this.second;
      this.hour = 60 * this.minute;
      this.day = 24 * this.hour;

      const numberTransitionDuration = getComputedStyle(this).getPropertyValue('--countdown-number-transition-duration');
      if (numberTransitionDuration) {
        this.numberTransitionDurationMS = CountdownTimer.durationToMS(numberTransitionDuration);
        this.animated = this.numberTransitionDurationMS > 0;
      }

      // add transition elements
      if (this.animated) {
        this.querySelectorAll('.countdown__number:is(.js-hours, .js-mins, .js-secs) .countdown__number-part').forEach((el) => {
          el.innerHTML = '';
          const span1 = document.createElement('span');
          span1.className = 'countdown__number-part-current';
          span1.textContent = '0';
          const span2 = document.createElement('span');
          span2.className = 'countdown__number-part-previous';
          span2.textContent = '0';
          el.append(span1, span2);
        });
      }

      this.interval = setInterval(this.timer.bind(this), this.second);
      this.timer();
      this.setAttribute('loaded', '');
    }

    initCompact() {
      this.daySuffix = this.dataset.daySuffix || '';
      this.hourSuffix = this.dataset.hourSuffix || '';
      this.minuteSuffix = this.dataset.minuteSuffix || '';
      this.secondSuffix = this.dataset.secondSuffix || '';
      this.interval = setInterval(this.compactTimer.bind(this), 1000);
      this.compactTimer();
      this.setAttribute('loaded', '');
    }

    compactTimer() {
      const timeDiff = this.endDate - new Date();
      const pad = (value) => String(value).padStart(2, '0');
      // each value carries its own unit, e.g. 1D 04H 12M 33S; days are left out under 24 hours
      const format = (hours, mins, secs) => `${pad(hours)}${this.hourSuffix} ${pad(mins)}${this.minuteSuffix} ${pad(secs)}${this.secondSuffix}`;

      if (timeDiff < 0) {
        this.compactEl.textContent = format(0, 0, 0);
        this.end();
        return;
      }

      const total = Math.floor(timeDiff / 1000);
      const days = Math.floor(total / 86400);
      const hms = format(Math.floor((total % 86400) / 3600), Math.floor((total % 3600) / 60), total % 60);
      this.compactEl.textContent = days > 0 ? `${days}${this.daySuffix} ${hms}` : hms;
    }

    end() {
      clearInterval(this.interval);
      this.setAttribute('loaded', '');
      if (this.reloadOnEnd) {
        CountdownTimer.scheduleReload(this.renderedEndDate);
      }
    }

    timer() {
      const timeDiff = this.endDate - new Date();

      if (timeDiff < 0) {
        this.end();
        return;
      }

      if (!this.daysEl) return;

      const days = Math.floor(timeDiff / this.day);
      const hours = Math.floor(timeDiff / this.hour);
      const mins = Math.floor(timeDiff / this.minute);
      const secs = Math.floor(timeDiff / this.second);

      if (days === 0) {
        this.daysEl.closest('.countdown__item').classList.add('countdown__item--is-0');
        this.daysEl.classList.add('countdown__number-part--is-0');
      }
      this.daysEl.textContent = days;

      const displayHours = String(hours - days * 24).padStart(2, '0');
      this.setPartValue(this.hoursPart1El, displayHours[0], true);
      this.setPartValue(this.hoursPart2El, displayHours[1]);

      const displayMins = String(mins - hours * 60).padStart(2, '0');
      this.setPartValue(this.minsPart1El, displayMins[0], true);
      this.setPartValue(this.minsPart2El, displayMins[1]);

      const displaySecs = String(secs - mins * 60).padStart(2, '0');
      this.setPartValue(this.secsPart1El, displaySecs[0], true);
      this.setPartValue(this.secsPart2El, displaySecs[1]);

      this.setAttribute('loaded', '');
    }

    setPartValue(part, value, setZeroClass) {
      if (setZeroClass) {
        part.classList.toggle('countdown__number-part--is-0', value === '0');
      }

      // Changing value and animated
      if (this.animated) {
        const current = part.querySelector('.countdown__number-part-current');
        if (value !== current.textContent) {
          const previous = part.querySelector('.countdown__number-part-previous');
          part.classList.add('countdown__number-part--start-transition');
          previous.textContent = current.textContent;
          current.textContent = value;
          setTimeout(() => {
            requestAnimationFrame(() => part.classList.remove('countdown__number-part--start-transition'));
          }, 20);
        }
      } else {
        part.textContent = value;
      }
    }

    /**
     * Reloads the page so the server re-renders it after the end time (e.g. a drop unlocking).
     * A random delay spreads the traffic spike, a cache-busting parameter skips cached HTML, and
     * a per-page cap for the session in sessionStorage means it can never loop.
     * @param {number} endTime - End time in ms since the epoch, as rendered by the server.
     */
    static scheduleReload(endTime) {
      if (CountdownTimer.reloadScheduled || (window.Shopify && window.Shopify.designMode)) return;

      const url = new URL(window.location.href);
      const storageKey = `countdown-reload-${endTime}-${url.pathname}`;
      try {
        const reloads = Number(sessionStorage.getItem(storageKey)) || 0;
        if (reloads >= CountdownTimer.maxReloadsPerPage) return;
        sessionStorage.setItem(storageKey, String(reloads + 1));
      } catch (e) {
        // without storage, the cache-busting parameter is the loop guard: one reload only
        if (url.searchParams.has(CountdownTimer.reloadParam)) return;
      }

      CountdownTimer.reloadScheduled = true;
      const { reloadDelayMin, reloadDelayMax } = CountdownTimer;
      const delay = reloadDelayMin + Math.random() * (reloadDelayMax - reloadDelayMin);
      // wait (up to a minute) while the shopper is typing in a field, so their input isn't lost
      const reload = (attempt = 0) => {
        const active = document.activeElement;
        if (attempt < 20 && active && active.matches('input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]), textarea, [contenteditable="true"]')) {
          setTimeout(() => reload(attempt + 1), 3000);
          return;
        }
        url.searchParams.set(CountdownTimer.reloadParam, Date.now());
        window.location.replace(url.toString());
      };
      setTimeout(reload, delay);
    }

    /**
     * Removes the cache-busting parameter from the address bar after a drop reload, so it isn't
     * shared or tracked. Kept when sessionStorage is unavailable, as it is then the loop guard.
     */
    static stripReloadParam() {
      const url = new URL(window.location.href);
      if (!url.searchParams.has(CountdownTimer.reloadParam)) return;
      try {
        sessionStorage.setItem('countdown-reload-check', '1');
        sessionStorage.removeItem('countdown-reload-check');
      } catch (e) {
        return;
      }
      url.searchParams.delete(CountdownTimer.reloadParam);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    static durationToMS(duration) {
      if (duration.indexOf('ms') > 0) {
        return parseInt(duration, 10);
      }

      if (duration.indexOf('s') > 0) {
        return parseFloat(duration) * 1000;
      }

      throw new Error(`Duration unit not recognised: ${duration}`);
    }
  }

  CountdownTimer.reloadScheduled = false;
  CountdownTimer.reloadParam = 'drop_reload';
  CountdownTimer.maxReloadsPerPage = 2;
  CountdownTimer.reloadDelayMin = 1000;
  CountdownTimer.reloadDelayMax = 5000;

  customElements.define('countdown-timer', CountdownTimer);
  CountdownTimer.stripReloadParam();
}
